import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import * as ActionTypes from '../../../constants/action-types';
import { UserContext, EntitiesContext } from '../../../App';
import { useCreateOrder } from '../../../hooks/useOrder';
import { useUpdateInventory } from '../../../hooks/useInventory';
import OrderRecord from '../../../records/order';
import { processingFee } from '../../../constants/config';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import { faTag } from '@fortawesome/free-solid-svg-icons';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { faDollar} from '@fortawesome/free-solid-svg-icons';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const icon = <FontAwesomeIcon icon={faQrcode} />
const tagIcon = <FontAwesomeIcon icon={faTag} />
const ccIcon = <FontAwesomeIcon icon={faCreditCard} />
const dollarIcon = <FontAwesomeIcon icon={faDollar} />
const timesIcon = <FontAwesomeIcon icon={faTimes} />
const checkIcon = <FontAwesomeIcon icon={faCheck} />

export default function Checkout({
  id,
  credit,
  cash,
  scan,
  search,
  setSearch,
  paymentMethod,
  setPaymentMethod,
  setRefetchId,
  sale
}) {
  const user = useContext(UserContext);
  const entities = useContext(EntitiesContext);
  const createOrder = useCreateOrder();
  const updateInventory = useUpdateInventory();
  const [isSoldAlert, setIsSoldAlert] = useState();
  const [cancel, setCancel] = useState(false);
  const [error, setError] = useState();
  const items = entities.state.get('inventory');

  // check if a sold item is added, display alert
  useEffect(() => {
    const sold = items.find(i => i.order_id);
    const order = entities.state.get('order').get(sold?.order_id);

    if (sold && (order && order.status === 'paid')) {
      setIsSoldAlert(sold);
    }
  }, [items]);

  const toggleCancel = () => {
    setCancel(!cancel);
  }

  // Check if discount should be applied based on sale settings
  const isDiscountActive = () => {
    if (!sale?.discount?.active) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

    // Parse discount time (format: "HH:MM")
    const [hours, minutes] = sale.discount.time.split(':').map(Number);
    const discountTime = hours * 60 + minutes;

    // Discount is active if current time is >= discount time
    return currentTime >= discountTime;
  }

  // Calculate price for an item, applying discount if applicable
  const getItemPrice = (item) => {
    if (item.discounted && isDiscountActive()) {
      const discountAmount = sale.discount.amount / 100;
      return item.price * (1 - discountAmount);
    }
    return item.price;
  }

  const totalAmount = () => {
    return items.reduce((partialSum, a) => partialSum + getItemPrice(a), 0);
  }

  const totalDiscount = () => {
    if (!isDiscountActive()) return 0;
    return items.reduce((sum, item) => {
      if (item.discounted) {
        const discountAmount = sale.discount.amount / 100;
        return sum + (item.price * discountAmount);
      }
      return sum;
    }, 0);
  }

  const calcedProcessingFee = () => {
    if (paymentMethod !== 'CREDIT_CARD') return 0;
    return parseFloat((totalAmount() * processingFee).toFixed(2));
  }

  const clearSoldItem = () => {
    remove(isSoldAlert.id);
    setIsSoldAlert(null);
  }

  const startOrder = () => {
    if (items.count() < 1) return false;

    createOrder.mutateAsync(new OrderRecord({
      account_id: user.user.id,
      payment_method: paymentMethod,
      processing_fee: calcedProcessingFee()
    }))
    // call checkout to Square POS when we have an order id
    .then(res => checkout(res.data.data));
  }

  // remove an inventory item from the entities state
  const remove = id => {
    entities.dispatch({
      type: ActionTypes.CLEAR_ENTITY,
      payload: {
        type: 'inventory',
        id: id
      }
    });
  }

  const redirect = data => {
    // dev: skip square processing, open order completion tab with url Sqaure app would redirect to
    if (process.env.NODE_ENV === 'development') {
      window.open('http://localhost:3001/admin/complete?data=' + JSON.stringify({ state: data.state, transaction_id: 1 }), '_blank');
    // prod: redirect to Square app on device with expected payload
    } else {
      window.location = "square-commerce-v1://payment/create?data=" + encodeURIComponent(JSON.stringify(data));
    }
  }

  // set order id to state for refetch polling
  // attach inventory items to order (wrapped up promises that update to API)
  // redirect to Square POS app
  const checkout = data => {
    const orderId = data.id;

    // persist new order id to localstorage for exception recovery
    window.localStorage.setItem('orderId', orderId);

    if (items.count() < 1 || !orderId) return false;

    setRefetchId(orderId);
    setIsSoldAlert(false);

    const dataParameter = {
      amount_money: {
        amount:        parseInt((totalAmount() + calcedProcessingFee()) * 100),
        currency_code: "USD"
      },

      callback_url: "https://sale.waitsfieldpta.org/admin/complete",

      client_id: "sq0idp-etddgE83nU_9t69iHanVWQ",
      // this is important, it's our order id that we get back at /admin/complete when Square returns us to the browser
      // and is how we know which order to update to paid status
      state: orderId.toString(),

      version: "1.3",
      notes: "WES PTA Sale - Order #" + orderId.toString(),
      options: {
        //supported_tender_types: ["CREDIT_CARD","CASH","CARD_ON_FILE"],
        supported_tender_types: [paymentMethod],
        auto_return: true
      }
    };

    Promise.all(items.map(i => updateInventory.mutateAsync(i.set('order_id', orderId))))
      .then(() => redirect(dataParameter))
      .catch(err => setError(err));
  }

  return (
    <div className="checkout-container">
      {error && <div className="notification is-danger">
        There was a problem submitting this order: {error}
      </div>}
      <div className="mt-2 has-text-left inventory">
        <table className="table is-fullwidth">
          <thead>
            <tr>
              <td>ID</td>
              <td>Item</td>
              <td>Price</td>
              <td></td>
            </tr>
          </thead>
          <tbody>
            {items.valueSeq().map((o, i) => <tr key={o.id}>
              <td>{o.id}</td>
              <td>
                {o.name}
                {o.discounted && isDiscountActive() && <span className="tag is-success is-light ml-1">-{sale.discount.amount}%</span>}
              </td>
              <td>
                {o.discounted && isDiscountActive() ? (
                  <span>
                    <span style={{textDecoration: 'line-through', opacity: 0.5}}>${o.price}</span>
                    {' '}
                    <span className="has-text-success has-text-weight-bold">${getItemPrice(o).toFixed(2)}</span>
                  </span>
                ) : (
                  <span>${o.price}</span>
                )}
              </td>
              <td><button className="button is-small is-danger" onClick={() => remove(o.id)}>
                <span className="icon is-small">
                  {timesIcon}
                </span>
              </button></td>
            </tr>)}
          </tbody>
        </table>
      </div>

      <div className={classNames("modal", {
          "is-active": isSoldAlert
        })}>
        <div className="modal-background"></div>
        <div className="modal-content">
          {isSoldAlert && <div className="notification is-danger has-text-centered">
            <p>The item <strong>{isSoldAlert.name}</strong> has already been sold.</p>
            <button className="button is-white mt-1" onClick={clearSoldItem}>OK</button>
            <button className="modal-close is-large" onClick={clearSoldItem} aria-label="close"></button>
          </div>}
        </div>
      </div>

      <div className={classNames("modal", {
          "is-active": cancel
        })}>
        <div className="modal-background"></div>
        <div className="modal-content">
          <div className="notification is-danger has-text-centered">
            <p>Are you sure you want to cancel this order?</p>
            <Link to="/admin" className="button is-white mt-1 mr-1">Yes</Link>
            <button className="button is-white mt-1" onClick={toggleCancel}>No</button>
            <button className="modal-close is-large" onClick={toggleCancel} aria-label="close"></button>
          </div>
        </div>
      </div>

      <div className="footer p-2">
        <table className="table is-bordered is-fullwidth mb-2">
          <tbody>
            <tr>
              <td align="right">
                items:<br/>
                {isDiscountActive() && totalDiscount() > 0 && <>discount (-{sale.discount.amount}%):<br/></>}
                subtotal:<br/>
                processing fee:<br/>
                total:<br/>
              </td>
              <td className="has-text-weight-semibold" align="left">
                {items.count()}<br/>
                {isDiscountActive() && totalDiscount() > 0 && <span className="has-text-success">-${totalDiscount().toFixed(2)}<br/></span>}
                ${totalAmount().toFixed(2)}<br/>
                ${calcedProcessingFee()}<br/>
                ${(totalAmount() + calcedProcessingFee()).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="columns is-variable is-1 is-mobile mb-0">
          <div className="column">
            <div className="field has-addons has-addons-fullwidth">
              <p className="control">
                <button className={classNames('button is-fullwidth', {
                    'is-selected': search === scan,
                    'is-link': search === scan
                  })} onClick={() => setSearch(search === scan ? null : scan)}>
                  <span className="icon is-small">
                    {icon}
                  </span>
                </button>
              </p>
              <p className="control">
                <button className={classNames('button is-fullwidth', {
                    'is-selected': search === id,
                    'is-link': search === id
                  })} onClick={() => setSearch(search === id ? null : id)}>
                  <span className="icon is-small">
                    {tagIcon}
                  </span>
                </button>
              </p>
            </div>
          </div>

          <div className="column">
            <div className="field has-addons has-addons-fullwidth">
              <p className="control">
                <button className={classNames('button is-fullwidth', {
                  'is-selected': paymentMethod === credit,
                  'is-link': paymentMethod === credit
                  })} onClick={() => setPaymentMethod(credit)}>
                  <span className="icon-text">
                    <span className="icon">
                      {ccIcon}
                    </span>
                  </span>
                </button>
              </p>
              <p className="control">
                <button className={classNames('button is-fullwidth', {
                    'is-selected': paymentMethod === cash,
                    'is-link': paymentMethod === cash
                  })} onClick={() => setPaymentMethod(cash)}>
                  <span className="icon-text">
                    <span className="icon">
                      {dollarIcon}
                    </span>
                  </span>
                </button>
              </p>
            </div>
          </div>

          <div className="column">
            <div className="field has-addons has-addons-fullwidth">
              <p className="control">
                <button className="button is-fullwidth" onClick={toggleCancel}>
                  <span className="icon-text">
                    <span className="icon">
                      {timesIcon}
                    </span>
                  </span>
                </button>
              </p>
              <p className="control">
                <a
                  className="button is-fullwidth"
                  disabled={createOrder.isLoading || items.count() < 1}
                  onClick={startOrder}>
                  <span className="icon-text">
                    <span className="icon">
                      {checkIcon}
                    </span>
                  </span>
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
